import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setFrontendApp } from '../../actions/infrastructures';
import axios from 'axios';
import { message } from 'antd';
import { BASE_URL } from '../../config/api';
import { GitBranch, Settings, ArrowLeft, FileText, FileCode } from 'lucide-react';
import General from './Tabs/General';
import Branches from './Tabs/Branches';
import EnvVars from './Tabs/EnvVars';
import Logs from './Tabs/Logs';
import BuildSpec from './Tabs/BuildSpec';

const ClientSideApp = () => {
  const { projectId, appId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [amplifyAppId, setAmplifyAppId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const frontendApp = useSelector((state) => state?.infrastructures?.currentFrontendApp);
  
  useEffect(() => {
    dispatch(setFrontendApp(projectId, appId));
  }, [dispatch, projectId, appId]);

  // Look up Amplify app ID if we have the name but not the ID
  useEffect(() => {
    if (frontendApp?.appName && !amplifyAppId) {
      lookupAmplifyAppId();
    }
  }, [frontendApp?.appName, amplifyAppId]);

  const lookupAmplifyAppId = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://cloudify-prod.wizr.in/api/amplify/apps/lookup/${frontendApp.appName}`);
      const { appId } = response.data;
      setAmplifyAppId(appId);
      message.success(`Found Amplify app: ${frontendApp.appName}`);
    } catch (error) {
      console.error('Error looking up Amplify app ID:', error);
      message.error(`Failed to find Amplify app with name: ${frontendApp.appName}`);
    } finally {
      setLoading(false);
    }
  };

  if (!frontendApp) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }
  
  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'branches', name: 'Branches', icon: GitBranch },
    { id: 'env-vars', name: 'Environmental Variables', icon: FileText },
    { id: 'buildspec', name: 'Build Spec', icon: FileCode },
    { id: 'logs', name: 'Logs', icon: FileText },
  ];
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <General 
          frontendApp={frontendApp} 
        />;
      case 'branches':
        return (
          <Branches 
            appId={appId} 
            amplifyAppId={amplifyAppId} 
            branches={frontendApp?.branches}
            gitHubRepo={frontendApp?.gitHubRepo}
          />
        );
      case 'env-vars':
        return (
          <EnvVars 
            projectId={projectId} 
            appId={appId} 
            amplifyAppId={amplifyAppId} 
            appName={frontendApp?.appName}
            appStatus={frontendApp?.status}
            environmentVariables={frontendApp?.envVars}
          />
        );
      case 'logs':
        return <Logs amplifyAppId={amplifyAppId} />;
      case 'buildspec':
        return <BuildSpec frontendApp={{...frontendApp, amplifyAppId: amplifyAppId}} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen p-8 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 font-display mb-2">
            {frontendApp?.appName}
          </h1>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                      ${activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                </button>
              );
            })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ClientSideApp;